import React, { FormEvent, useRef } from "react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AmpLoadingSpinner from "../../components/common/AmpLoadingSpinner/AmpLoadingSpinner";
import Step1 from "../../components/rfr/Steps/Step1";
import Step2 from "../../components/rfr/Steps/Step2";
import Step3 from "../../components/rfr/Steps/Step3";
import { RootState } from "../../redux/reducers/root";
import { submitRfr } from "../../redux/actions/rfr/rfr";
import { SubmitRfrState } from "../../redux/reducers/getRfrReducer/submitRfrReducer";

import "./RfRForm.css";
import { setLargeToast } from "../../redux/actions/toastNotification/toastNotification";

export interface RfrStepProps {
    formdata: Record<string, unknown>,
    setFormdata: (e) => void,
    handleStepChange: (e) => void,
    statedata: Record<string, unknown>
}

export default function RfRForm() {
    const { state } = useLocation();
    state.user_prop.cva = state.user_prop.cva.replace(/[^0-9]/g, '');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation("rfr");
    const submitresult = useSelector((state: RootState) => state.rfrReducer.submitrfr as SubmitRfrState);

    const [activeStep, setActiveStep] = useState("step1");
    const navtabRef = useRef();
    const data = {
        "notice_id": state.notice_id,
        "pdf_type": state.pdf_type,
        "step11RepEmailInput": state.useremail,
        "step11RepOtherEmailInput": state.useremail,
        "step22OwnerEmailInput": state.useremail,
        "step21RollInput": state.user_prop.roll_num,
        "step21ValueInput": state.user_prop.cva.replace(/[^0-9]/g, ''),
        "step21PropAddressInput": state.user_prop.address,
        "step21MunicipalityInput": state.user_prop.city,
        "pois": state.pois.map(({ poi_roll_num }) => poi_roll_num),
        "step31ReasonInput": [],
        "step32DocInput": [],
        "step11RepOtherFileInput": [],
        "step32FileInput": [],
        "disableSubmit": false
    };
    const [formdata, setFormdata] = useState(data);

    const saveFormdata = (stepdata, files, filekey) => {
        Object.keys(stepdata).forEach((key) => {
            formdata[key] = stepdata[key];
        });
        if (files !== null) {
            formdata[filekey] = files;
        }
        setFormdata(formdata);
    };

    const handleStepChange = (name, stepdata, files, filekey) => {
        saveFormdata(stepdata, files, filekey);
        navtabRef.current.scrollIntoView({ behavior: 'smooth' });
        if (name === "submit") {
            submitRfrForm();
            formdata.disableSubmit = true;
        } else {
            setActiveStep(name);
        }
    };

    const ChildComponent = () => {
        switch (activeStep) {
            case "step1":
                return <Step1 formdata={formdata} setFormdata={setFormdata} handleStepChange={handleStepChange} statedata={state} />;
            case "step2":
                return <Step2 formdata={formdata} setFormdata={setFormdata} handleStepChange={handleStepChange} statedata={state} />;
            case "step3":
                return <Step3 formdata={formdata} setFormdata={setFormdata} handleStepChange={handleStepChange} statedata={state} />;
            default:
                return <></>;
        }
    };

    const submitRfrForm = (e: FormEvent) => {
        dispatch(submitRfr(formdata));
    };

    useEffect(() => {
        const navigateToRfrLanding = () => {
            navigate("/amp/rfr", {
                // replace: true,
                state: { noticeid: submitresult.response.notice_id, success: submitresult.response.success }
            });
        };

        if(submitresult.status !== 200 && submitresult.error){
            const data = {...formdata};
            data.disableSubmit = false;
            setFormdata(data)
            dispatch(setLargeToast("Error Occured while submitting RFR. Please contact us.", "error", true))
        } else {
            if (submitresult.torfr === true) {
                navigateToRfrLanding();
                
                submitresult.torfr = false;
            }
        }

    }, [submitresult, navigate, dispatch, formdata]);

    return (
        <>
            <div className="rfr-landing">
                <h1>{t("rfr.landing.title1")}</h1>
                <p>{t("rfr.landing.content-1-1")}</p>
                <p>{t("rfr.landing.content-1-2")}</p>
                <p>{t("rfr.landing.content-1-3")}</p>
                <p>{t("rfr.landing.content-1-4")}</p>
                <h1>{t("rfr.landing.title2")}</h1>
                <p>{t("rfr.landing.content-2-1")}</p>
                <p>{t("rfr.landing.content-2-2")}</p>
                <p>{t("rfr.landing.content-2-3")}</p>
                <p></p>
            </div>
            <div className="rfr-landing">
                <div className="page-row">
                    <div className="tab-container">
                        <div className="navtab-parent" ref={navtabRef}>
                            <div id="step1tab" className={"navtab " + (activeStep === "step1" ? "active" : "")}>
                                <div>
                                    {t("rfr.rfrform.step1.title-1")}
                                    <br />
                                    {t("rfr.rfrform.step1.title-2")}
                                </div>
                            </div>
                            <div id="step2tab" className={"navtab " + (activeStep === "step2" ? "active" : "")}>
                                <div>
                                    {t("rfr.rfrform.step2.title-1")}
                                    <br />
                                    {t("rfr.rfrform.step2.title-2")}
                                </div>
                            </div>
                            <div id="step3tab" className={"navtab " + (activeStep === "step3" ? "active" : "")}>
                                <div>
                                    {t("rfr.rfrform.step3.title-1")}
                                    <br />
                                    {t("rfr.rfrform.step3.title-2")}
                                </div>
                            </div>
                        </div>
                        <AmpLoadingSpinner loading={submitresult.loading}>
                            <div className="tab-content">
                                <form className="rfr-form" onSubmit={(e) => { submitRfrForm(e) }}>
                                    <ChildComponent />
                                </form>
                            </div>
                        </AmpLoadingSpinner>
                    </div>
                </div>
            </div>
        </>
    )
}